'use client';
import { useState } from 'react';
import Image from 'next/image';
import { motion } from 'motion/react';
import { type LinkField } from 'lib/types';
import { cn } from 'lib/utils';
import { Button, type ButtonProps, RichText, Wrapper } from 'ui';

export type HeroInteriorProps = {
  eyebrow?: string;
  title: string;
  content?: string;
  background?: string;
  buttonLink?: LinkField;
  buttonLinkVariant?: ButtonProps['variant'];
  disableMotion?: boolean;
};

const HeroInterior = ({
  eyebrow,
  title,
  content,
  buttonLink,
  buttonLinkVariant = 'secondary',
  background,
  disableMotion = false,
}: HeroInteriorProps) => {
  const [bgIsLoaded, setBgIsLoaded] = useState(false);

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 1 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: {
      opacity: 0,
      y: 30,
      filter: 'blur(4px)',
    },
    visible: {
      opacity: 1,
      y: 0,
      filter: 'blur(0px)',
      transition: {
        duration: 0.6,
        ease: 'easeOut' as const,
      },
    },
  };

  return (
    <section className="relative z-2 bg-graphite-900 lg:px-5 lg:pb-4 lg:min-h-140 lg:flex">
      <Wrapper
        variant="header"
        className="relative flex flex-col justify-end pt-header-height-mobile lg:pt-header-height-full pb-wrapper-header-x"
      >
        {/* Content Area */}
        <motion.div
          className="grow relative z-4 flex flex-col lg:flex-row lg:items-center gap-6 lg:gap-18 xl:gap-20 pt-8"
          variants={disableMotion ? undefined : containerVariants}
          initial={disableMotion ? undefined : 'hidden'}
          animate={
            disableMotion ? undefined : bgIsLoaded ? 'visible' : 'hidden'
          }
        >
          {/* Eyebrow & Title Area */}
          <header className="lg:basis-1/2 lg:grow flex flex-col justify-center gap-6">
            {!!eyebrow && (
              <motion.h1
                className="text-eyebrow font-mono uppercase"
                variants={disableMotion ? undefined : itemVariants}
              >
                {eyebrow}
              </motion.h1>
            )}
            {eyebrow ? (
              <motion.h2
                className="text-h2"
                variants={disableMotion ? undefined : itemVariants}
              >
                {title}
              </motion.h2>
            ) : (
              <motion.h1
                className="text-h2"
                variants={disableMotion ? undefined : itemVariants}
              >
                {title}
              </motion.h1>
            )}
          </header>

          {/* Content & Button Area */}
          {(!!content || !!buttonLink?.text) && (
            <div className="lg:basis-1/2 lg:grow flex flex-col gap-8 lg:pt-4">
              {!!content && (
                <motion.div variants={disableMotion ? undefined : itemVariants}>
                  <RichText className="text-p1" {...{ content }} />
                </motion.div>
              )}

              {!!buttonLink?.text && (
                <motion.div
                  className="flex flex-col items-start lg:flex-row lg:flex-wrap gap-4"
                  variants={disableMotion ? undefined : itemVariants}
                >
                  <Button href={buttonLink.href} variant={buttonLinkVariant}>
                    {buttonLink.text}
                  </Button>
                </motion.div>
              )}
            </div>
          )}
        </motion.div>

        {!!background && (
          <>
            {/* Color Gradient */}
            <div
              className="z-2 absolute bottom-0 left-0 h-full w-1/2 lg:w-full bg-gradient-to-r from-black/50 to-transparent rounded-bl-lg"
              aria-hidden="true"
            />

            {/* Background Image */}
            <Image
              src={background}
              alt={title}
              fill={true}
              className={cn(
                'absolute size-full object-cover pointer-events-none rounded-b-lg transition-opacity duration-500 ease-in-out opacity-0',
                {
                  'opacity-100': bgIsLoaded,
                }
              )}
              priority
              onLoad={() => setBgIsLoaded(true)}
            />
          </>
        )}
      </Wrapper>
    </section>
  );
};

export default HeroInterior;
